import { useEffect, useState } from "react";
import { Run } from "./Run";
import { createEntityStore } from "../../common/entityStore";
import { today, withSameProp } from "../../common/utils";
import { Grid } from "@mui/material";
import { propDateField, propEnumField } from "../../common/entityStoreForm";
import { propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { RunLogEntry } from "./common";
import { LogEntryType, useDeliveryMethods } from "../../common/enum";
import { dayjs } from "../../common/config";

export const StarterRunPage = withSameProp("fake", () => (props: { fake?: boolean }) => {
    const { hasMultipleDeliveryMethods, isFetchingDeliveryMethods } = useDeliveryMethods(p => ({
        hasMultipleDeliveryMethods: p.items.filter(p => !p.inactive && (p.hasCoProjects || p.isDefault)).length > 1,
        isFetchingDeliveryMethods: p.fetching,
    }));
    const [useParamsStore] = useState(() => createEntityStore<RunParams>({
        data: { runDate: today(), invcDate: today(), dueDate: today() },
        id: null,
    }));
    useEffect(() => {
        useParamsStore.getState().setPropValidators([
            propValidValue("runDate", "runDateFormatted"),
            propValidValue("invcDate", "invcDateFormatted"),
            propValidValue("dueDate", "dueDateFormatted"),
            propRequired("runDate"),
            propRequired("invcDate"),
            propRequired("dueDate"),
            propValueValidator("invcDate", (p, e) => p && e.runDate && dayjs.tz(p).isBefore(dayjs.tz(e.runDate)) &&
                "Invoice date must be greater or equal to run date."),
            propValueValidator("dueDate", (p, e) => p && e.invcDate && dayjs.tz(p).isBefore(dayjs.tz(e.invcDate)) &&
                "Due date must be greater or equal to invoice date."),
            hasMultipleDeliveryMethods && propRequired("deliveryMethodId"),
        ]);
    }, [hasMultipleDeliveryMethods, useParamsStore]);
    const simpleDialog = useSimpleDialog();
    async function processResult(result: {
        logEntries: RunLogEntry[];
        invalidWpPriceProjectIds: number[] | null;
    }, runAgain: () => void) {
        const oldIds = useParamsStore.getState().clientData.ignoreInvalidWpPriceProjectIds || [];
        const newIds = result.invalidWpPriceProjectIds || [];
        if (newIds.length > 0) {
            const errors = result.logEntries.filter(p => p.type === LogEntryType.Error).map(p => p.message.trim()).join("\n\n").trim();
            simpleDialog.show("Warning", errors + "\n\nDo you want to continue?", undefined, yesNoButtons(() => {
                useParamsStore.getState().setClientDataProp("ignoreInvalidWpPriceProjectIds", oldIds.concat(newIds));
                runAgain();
            }));
        }
        else {
            useParamsStore.getState().setClientDataProp("ignoreInvalidWpPriceProjectIds", undefined);
        }
    }
    function renderParams() {
        return isFetchingDeliveryMethods ? <></> : <>
            <Grid item xs={12}>{propDateField(useParamsStore)("runDate", "runDateFormatted", "Run date")}</Grid>
            <Grid item xs={12}>{propDateField(useParamsStore)("invcDate", "invcDateFormatted", "Invoice date")}</Grid>
            <Grid item xs={12}>{propDateField(useParamsStore)("dueDate", "dueDateFormatted", "Due date")}</Grid>
            {hasMultipleDeliveryMethods && <Grid item xs={12}>{propEnumField(useParamsStore)("deliveryMethodId", "Delivery method", useDeliveryMethods)}</Grid>}
        </>;
    }
    return <>
        {simpleDialog.render()}
        <Run name="Starter run" fake={props.fake} hub="starterRun"
            useParamsStore={useParamsStore} renderParams={renderParams} processResult={processResult} />
    </>;
});

interface RunParams {
    runDate: string;
    runDateFormatted?: string;
    invcDate: string;
    invcDateFormatted?: string;
    dueDate: string;
    dueDateFormatted?: string;
    ignoreInvalidWpPriceProjectIds?: number[];
    deliveryMethodId?: number | null;
}
